import { useEffect, useRef, useState } from "react";
import { SEGMENT_POLL_CARDS_S, SEGMENT_POLL_LISTING_S } from "../../shared/limits.ts";
import type { Segment } from "../../shared/price.ts";
import { api } from "./api.ts";
import { syncClock } from "./clock.ts";

/** Polls one listing's segment every 15 s, on focus, and on demand (R-BRW-6). The initial
 *  segment comes from the page payload; polled values win once they arrive. */
export function useSegment(id: string, initial: Segment | null) {
  const [polled, setPolled] = useState<Segment | null>(null);
  const refresh = useRef(async () => {
    try {
      const s = await api<Segment>(`/api/listings/${id}/segment`);
      syncClock(s.server_ts);
      setPolled(s);
    } catch {
      /* keep the last one */
    }
  });
  useEffect(() => {
    if (initial) syncClock(initial.server_ts);
  }, [initial]);
  useEffect(() => {
    const tick = () => void refresh.current();
    const int = setInterval(tick, SEGMENT_POLL_LISTING_S * 1000);
    window.addEventListener("focus", tick);
    return () => {
      clearInterval(int);
      window.removeEventListener("focus", tick);
    };
  }, [id]);
  return { seg: polled ?? initial, refresh: () => refresh.current() };
}

/** Polls segments for a set of visible cards every 30 s (one request per page). */
export function useSegments(ids: string[]) {
  const [map, setMap] = useState<Record<string, Segment>>({});
  const key = ids.join(",");
  useEffect(() => {
    if (!key) return;
    let live = true;
    const load = () =>
      api<{ segments: Record<string, Segment>; server_ts: number }>(
        `/api/listings/segments?ids=${key}`,
      )
        .then((r) => {
          if (!live) return;
          syncClock(r.server_ts);
          setMap((m) => ({ ...m, ...r.segments }));
        })
        .catch(() => undefined);
    void load();
    const int = setInterval(load, SEGMENT_POLL_CARDS_S * 1000);
    window.addEventListener("focus", load);
    return () => {
      live = false;
      clearInterval(int);
      window.removeEventListener("focus", load);
    };
  }, [key]);
  return map;
}
